/**
 * The truncation warning that stays put (CR-7432).
 *
 * A toast fires at the moment of download and then disappears; by the time the
 * user opens the file and wonders whether it is complete, there is nothing
 * left to tell them. This banner sits in the drill-down the export came from
 * and holds both numbers until it is dismissed.
 *
 * @link lib/exportTruncation for the response-header reader
 */
import { AlertTriangle, X } from 'lucide-react';

import { ExportTruncation, exportTruncationMessage } from '../../lib/exportTruncation';

interface ExportTruncationBannerProps {
  /** Counts from the last export, or null when it was complete. */
  truncation: ExportTruncation | null;
  /** Omit to render the banner without a dismiss control. */
  onDismiss?: () => void;
}

/**
 * Amber inline banner naming the exported count and the true total.
 * Renders nothing when the last export was complete.
 */
export default function ExportTruncationBanner({
  truncation,
  onDismiss,
}: ExportTruncationBannerProps) {
  if (!truncation) return null;

  return (
    <div
      role="status"
      data-testid="export-truncation-banner"
      className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
      <span className="flex-1">{exportTruncationMessage(truncation)}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss export warning"
          className="shrink-0 rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
